import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import {
  MAX_COMPS, emptyManualForm, validateManualComp, subjectToForm, buildGenerateRequest,
  compCountStatus, initialSelection, manualSaleDateBounds,
} from '../../utils/cmaReview';
import './CmaReviewModal.css';

// Same helper every page defines locally (see CmaPricingCard.js).
const getFirebaseToken = () => new Promise((resolve, reject) => {
  if (auth.currentUser) {
    auth.currentUser.getIdToken(true).then(resolve).catch(reject);
    return;
  }
  const timeoutId = setTimeout(() => { unsub(); reject(new Error('Firebase auth timeout')); }, 10000);
  const unsub = auth.onAuthStateChanged((user) => {
    if (user) {
      clearTimeout(timeoutId);
      unsub();
      user.getIdToken(true).then(resolve).catch(reject);
    }
  });
});

const DRAFT_TIMEOUT_MS = 180_000;    // candidate search hits ATTOM/MLS and the verifier
const GENERATE_TIMEOUT_MS = 120_000;

const money = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? `$${Math.round(n).toLocaleString('en-US')}` : '—';
};

// axios cancellation without importing axios (its ESM entry does not load under Jest).
const isCanceled = (err) => err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError';

// The service answers request-validation failures as { error: 'Validation Failed', errors: { field: msg } }.
const firstFieldError = (data) => {
  const errs = data?.errors;
  if (!errs || typeof errs !== 'object') return null;
  const [field, msg] = Object.entries(errs)[0] || [];
  return msg ? `${field}: ${msg}` : null;
};

const errorMessage = (err, fallback) => {
  const data = err?.response?.data;
  return data?.message || data?.detail?.message || firstFieldError(data) || data?.error || fallback;
};

/**
 * Comp review before a CMA is valued: the agent sees the homes the analysis picked (and the ones it
 * considered), keeps or drops them, adds homes with their own sale values, corrects the subject and adds
 * upgrades, then generates. Each generation is a new report version; reopening a report's draft pre-fills
 * the previous choices.
 *
 * Props: transaction, address, draftId (optional - reopen), onClose(), onGenerated(report)
 */
const CmaReviewModal = ({ transaction, address, draftId: initialDraftId, onClose, onGenerated }) => {
  const [phase, setPhase] = useState('loading');           // loading | review | error
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [manualComps, setManualComps] = useState([]);       // [{ key, comp }]
  const [subjectForm, setSubjectForm] = useState(subjectToForm());
  const [upgrades, setUpgrades] = useState([]);              // [{ key, description, value }]
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyManualForm());
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const abortRef = useRef(null);
  const keyRef = useRef(1);
  const bounds = useMemo(() => manualSaleDateBounds(), []);

  const applyDraft = useCallback((d) => {
    setDraft(d);
    setSelected(initialSelection(d));
    setSubjectForm(subjectToForm(d.subjectProperty));
    const prev = d.lastRequest || {};
    setManualComps((prev.manualComps || []).map((comp) => ({ key: keyRef.current++, comp })));
    const o = prev.subjectOverrides;
    if (o) setSubjectForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(o).filter(([k]) => k !== 'upgrades')) }));
    setUpgrades(((o && o.upgrades) || []).map((u) => ({ key: keyRef.current++, description: u.description, value: String(u.value) })));
    setPhase('review');
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const headers = { Authorization: `Bearer ${token}` };
        if (initialDraftId) {
          try {
            const res = await apiClient.get(API_ENDPOINTS.cmaDraftById(initialDraftId), { headers, signal: controller.signal });
            applyDraft(res.data);
            return;
          } catch (err) {
            if (isCanceled(err)) return;
            // Expired or not ours: fall through and start a fresh review of the same address.
          }
        }
        const res = await apiClient.post(
          API_ENDPOINTS.cmaDraft,
          { address, transactionId: transaction.id },
          { headers, timeout: DRAFT_TIMEOUT_MS, signal: controller.signal, retry: { retries: 1 } },
        );
        applyDraft(res.data);
      } catch (err) {
        if (isCanceled(err)) return;
        console.error('CMA draft failed:', err);
        setLoadError(err.code === 'ECONNABORTED'
          ? 'Finding comparable sales is taking longer than expected. Please try again.'
          : errorMessage(err, 'Unable to find comparable sales for this property. Please try again.'));
        setPhase('error');
      }
    })();
    return () => controller.abort();
  }, [address, transaction.id, initialDraftId, applyDraft]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const status = compCountStatus(selected.size, manualComps.length);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const addManual = () => {
    const { errors, comp } = validateManualComp(form);
    setFormErrors(errors);
    if (!comp) return;
    setManualComps((prev) => [...prev, { key: keyRef.current++, comp }]);
    setForm(emptyManualForm());
    setShowAdd(false);
  };

  const generate = async () => {
    if (!status.canGenerate || submitting) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      const token = await getFirebaseToken();
      const body = buildGenerateRequest({
        selectedIds: selected, manualComps, subject: draft.subjectProperty, subjectForm, upgrades,
      });
      const res = await apiClient.post(API_ENDPOINTS.cmaDraftGenerate(draft.draftId), body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: GENERATE_TIMEOUT_MS,
      });
      onGenerated && onGenerated(res.data);
      onClose();
    } catch (err) {
      console.error('CMA generate failed:', err);
      setSubmitError(errorMessage(err, 'Unable to generate the CMA. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const candidates = draft?.candidates || [];

  return (
    <div className="cma-review-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="cma-review-modal" role="dialog" aria-modal="true" aria-labelledby="cma-review-title">
        <div className="cma-review-header">
          <div>
            <h2 id="cma-review-title">Review comparable homes</h2>
            <p className="cma-review-address">{address}</p>
          </div>
          <button type="button" className="cma-review-close" aria-label="Close" onClick={onClose} disabled={submitting}>×</button>
        </div>

        {phase === 'loading' && (
          <div className="cma-review-state">
            <div className="txn-cma-loading-spinner" />
            <p>Finding comparable sales… this can take up to a minute.</p>
            <button type="button" className="cma-review-link-btn" onClick={onClose}>Cancel</button>
          </div>
        )}

        {phase === 'error' && (
          <div className="cma-review-state">
            <p className="cma-review-error">{loadError}</p>
            <button type="button" className="cma-review-secondary" onClick={onClose}>Close</button>
          </div>
        )}

        {phase === 'review' && draft && (
          <>
            <div className="cma-review-body">
              {draft.dataQualityNote && <div className="cma-review-note">{draft.dataQualityNote}</div>}

              <section className="cma-review-section">
                <h3>Subject property</h3>
                <p className="cma-review-hint">Correct anything the records got wrong. Changes re-score every comparable.</p>
                <div className="cma-review-grid">
                  {[['bedrooms', 'Beds'], ['bathrooms', 'Baths'], ['squareFeet', 'Sq ft'], ['lotSizeSqFt', 'Lot sq ft'], ['yearBuilt', 'Year built']].map(([k, label]) => (
                    <label key={k} className="cma-review-field">
                      <span>{label}</span>
                      <input inputMode="numeric" value={subjectForm[k]} onChange={(e) => setSubjectForm((f) => ({ ...f, [k]: e.target.value }))} />
                    </label>
                  ))}
                  <label className="cma-review-field">
                    <span>Type</span>
                    <input value={subjectForm.propertyType} onChange={(e) => setSubjectForm((f) => ({ ...f, propertyType: e.target.value }))} />
                  </label>
                </div>

                <h4>Upgrades</h4>
                <p className="cma-review-hint">Improvements you value yourself (kitchen remodel, new roof…). The amount is added to each value estimate.</p>
                {upgrades.map((u) => (
                  <div className="cma-review-upgrade-row" key={u.key}>
                    <input placeholder="Description" maxLength={120} value={u.description}
                      onChange={(e) => setUpgrades((prev) => prev.map((x) => (x.key === u.key ? { ...x, description: e.target.value } : x)))} />
                    <input placeholder="Value ($)" inputMode="numeric" value={u.value}
                      onChange={(e) => setUpgrades((prev) => prev.map((x) => (x.key === u.key ? { ...x, value: e.target.value } : x)))} />
                    <button type="button" className="cma-review-link-btn" onClick={() => setUpgrades((prev) => prev.filter((x) => x.key !== u.key))}>Remove</button>
                  </div>
                ))}
                {upgrades.length < 20 && (
                  <button type="button" className="cma-review-link-btn"
                    onClick={() => setUpgrades((prev) => [...prev, { key: keyRef.current++, description: '', value: '' }])}>
                    + Add an upgrade
                  </button>
                )}
              </section>

              <section className="cma-review-section">
                <h3>Comparable sales found</h3>
                <p className="cma-review-hint">Suggested homes are checked. Uncheck any you do not want, or check others that were considered.</p>
                <div className="cma-review-table-wrap">
                  <table className="cma-review-table">
                    <thead>
                      <tr><th /><th>Address</th><th>Bd/Ba</th><th>Sq ft</th><th>Sold</th><th>Price</th><th>Distance</th><th>Match</th></tr>
                    </thead>
                    <tbody>
                      {candidates.map((c) => (
                        <tr key={c.compId} className={selected.has(c.compId) ? 'is-selected' : ''}>
                          <td>
                            <input type="checkbox" aria-label={`Use ${c.address}`} checked={selected.has(c.compId)}
                              onChange={() => toggle(c.compId)} />
                          </td>
                          <td>
                            <div className="cma-review-addr">{c.address}</div>
                            <div className="cma-review-sub">
                              {[c.city, c.state, c.zip].filter(Boolean).join(' ')}
                              {c.autoSelected && <span className="cma-review-tag cma-review-tag-suggested">Suggested</span>}
                              {c.excludedReason && <span className="cma-review-tag cma-review-tag-removed" title={c.excludedReason}>Not suggested</span>}
                            </div>
                            {c.excludedReason && <div className="cma-review-reason">{c.excludedReason}</div>}
                          </td>
                          <td>{c.bedrooms ?? '—'} / {c.bathrooms ?? '—'}</td>
                          <td>{c.squareFeet != null ? c.squareFeet.toLocaleString() : '—'}</td>
                          <td>{c.saleDate || '—'}</td>
                          <td>{money(c.salePrice)}</td>
                          <td>{c.distanceMiles != null ? `${c.distanceMiles.toFixed(1)} mi` : '—'}</td>
                          <td>{c.matchGrade ? `${c.matchGrade}${c.similarityScore != null ? ` · ${Number(c.similarityScore).toFixed(0)}` : ''}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="cma-review-section">
                <h3>Homes you add</h3>
                <p className="cma-review-hint">Add a recent sale you know about. It is scored like the others and marked “agent-provided” in the report.</p>
                {manualComps.map(({ key, comp }) => (
                  <div className="cma-review-manual-row" key={key}>
                    <div>
                      <div className="cma-review-addr">{comp.address}</div>
                      <div className="cma-review-sub">{money(comp.salePrice)} · sold {comp.saleDate} · {comp.squareFeet.toLocaleString()} sq ft</div>
                    </div>
                    <button type="button" className="cma-review-link-btn" onClick={() => setManualComps((prev) => prev.filter((m) => m.key !== key))}>Remove</button>
                  </div>
                ))}
                {!showAdd && manualComps.length < 10 && (
                  <button type="button" className="cma-review-secondary" onClick={() => setShowAdd(true)}>+ Add a home</button>
                )}
                {showAdd && (
                  <div className="cma-review-add-form">
                    <div className="cma-review-grid">
                      {[['address', 'Address *', 'text', 'wide'], ['city', 'City', 'text'], ['state', 'State', 'text'], ['zip', 'ZIP', 'text'],
                        ['salePrice', 'Sale price ($) *', 'text'], ['saleDate', 'Sale date *', 'date'], ['squareFeet', 'Sq ft *', 'text'],
                        ['bedrooms', 'Beds', 'text'], ['bathrooms', 'Baths', 'text'], ['yearBuilt', 'Year built', 'text'],
                        ['notes', 'Notes', 'text', 'wide']].map(([name, label, type, wide]) => (
                        <label key={name} className={`cma-review-field${wide ? ' is-wide' : ''}`}>
                          <span>{label}</span>
                          <input type={type} value={form[name]} min={type === 'date' ? bounds.min : undefined}
                            max={type === 'date' ? bounds.max : undefined}
                            onChange={(e) => setField(name, e.target.value)} />
                          {formErrors[name] && <em className="cma-review-field-error">{formErrors[name]}</em>}
                        </label>
                      ))}
                    </div>
                    <div className="cma-review-actions-inline">
                      <button type="button" className="cma-review-primary" onClick={addManual}>Add home</button>
                      <button type="button" className="cma-review-link-btn" onClick={() => { setShowAdd(false); setFormErrors({}); setForm(emptyManualForm()); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </section>
            </div>

            <div className="cma-review-footer">
              <div className={`cma-review-count${status.overLimit ? ' is-warning' : ''}${!status.canGenerate ? ' is-error' : ''}`}>
                {status.message}
                {draft.version > 0 && <span className="cma-review-version"> · Will create version {draft.version + 1}</span>}
              </div>
              {submitError && <div className="cma-review-error">{submitError}</div>}
              <div className="cma-review-actions">
                <button type="button" className="cma-review-link-btn" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="button" className="cma-review-primary" onClick={generate} disabled={!status.canGenerate || submitting}>
                  {submitting ? 'Generating…' : 'Generate report'}
                </button>
              </div>
              <div className="cma-review-fineprint">Up to {MAX_COMPS} comparables are used. Agent-provided values are not independently verified.</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CmaReviewModal;
